import React, { useState } from 'react';
import {
  Box,
  Heading,
  Button,
  HStack,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Divider,
  Spinner,
  Center,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import { 
  RepeatIcon, 
  InfoIcon,
} from '@chakra-ui/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';

const CRMSyncPage: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // State for sync modal
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // Fetch CRM sync stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['crm-sync-stats'],
    queryFn: () => apiService.getCRMSyncStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch sync logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['crm-sync-logs', page, pageSize],
    queryFn: () => apiService.getCRMSyncLogs({
      page,
      page_size: pageSize,
      sort_by: 'started_at',
      sort_order: 'desc'
    }),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Fetch contacts summary
  const { data: contactsSummary } = useQuery({
    queryKey: ['crm-contacts-summary'],
    queryFn: () => apiService.getCRMContactsSummary(),
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: () => apiService.testCRMConnection(),
    onSuccess: (data) => {
      toast({
        title: 'Connexion réussie',
        description: data.message,
        status: 'success',
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur de connexion',
        description: error.message || 'Impossible de se connecter au CRM',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => apiService.initiateCRMSync(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['crm-sync-logs'] });
      toast({
        title: 'Synchronisation initiée',
        description: `${data.message} (durée estimée: quelques secondes)`,
        status: 'success',
        duration: 5000,
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur de synchronisation',
        description: error.message || 'Impossible d\'initier la synchronisation',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const handleSync = () => {
    syncMutation.mutate();
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'running': return 'blue';
      case 'failed': return 'red';
      case 'cancelled': return 'orange';
      default: return 'gray';
    }
  };

  const getSyncStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'running': return 'En cours';
      case 'failed': return 'Échec';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'contacts': return 'Contacts';
      case 'events': return 'Événements';
      case 'full': return 'Complète';
      default: return type;
    }
  };

  const runningSync = logsData?.items.find(log => log.status === 'running');

  if (statsLoading) {
    return (
      <Center py={10}>
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex mb={6} align="center">
        <VStack align="start" spacing={2}>
          <Heading>Synchronisation CRM</Heading>
          <Text color="gray.600" fontSize="sm">
            Synchronisation avec SuiteCRM
          </Text>
        </VStack>
        <Spacer />
        <HStack spacing={3}>
          <Button
            leftIcon={<InfoIcon />}
            variant="outline"
            onClick={() => testConnectionMutation.mutate()}
            isLoading={testConnectionMutation.isPending}
          >
            Tester la connexion
          </Button>
          <Button
            leftIcon={<RepeatIcon />}
            colorScheme="blue"
            onClick={onOpen}
            isDisabled={!!runningSync}
          >
            Nouvelle synchronisation
          </Button>
        </HStack>
      </Flex>

      {/* Running Sync Alert */}
      {runningSync && (
        <Alert status="info" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>Synchronisation en cours</AlertTitle>
            <AlertDescription>
              Synchronisation {getTypeLabel(runningSync.sync_type)} démarrée le {formatDate(runningSync.started_at)}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Stats Grid */}
      {statsData && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Dernière sync réussie</StatLabel>
                <StatNumber fontSize="lg">
                  {statsData.last_successful_sync 
                    ? formatDate(statsData.last_successful_sync)
                    : 'Jamais'
                  }
                </StatNumber>
                <StatHelpText>
                  Taux de succès: {statsData.sync_success_rate}%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Synchronisations aujourd'hui</StatLabel>
                <StatNumber>{statsData.total_syncs_today}</StatNumber>
                <StatHelpText>
                  Cette semaine: {statsData.total_syncs_this_week}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Durée moyenne</StatLabel>
                <StatNumber fontSize="lg">
                  {statsData.average_sync_duration_minutes 
                    ? `${statsData.average_sync_duration_minutes.toFixed(1)}m`
                    : '-'
                  }
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Statut connexion</StatLabel>
                <StatNumber fontSize="lg">
                  <Badge colorScheme="green">Connecté</Badge>
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Contacts Summary */}
      {contactsSummary && (
        <Card mb={6}>
          <CardHeader>
            <Heading size="md">Résumé des contacts</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              <Stat>
                <StatLabel>Total contacts</StatLabel>
                <StatNumber>{contactsSummary.local_contacts.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Synchronisés CRM</StatLabel>
                <StatNumber>{contactsSummary.local_contacts.synced_with_crm}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Contacts VIP</StatLabel>
                <StatNumber>{contactsSummary.local_contacts.vip_contacts}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Couverture sync</StatLabel>
                <StatNumber>{contactsSummary.sync_coverage.percentage_synced}%</StatNumber>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Sync Logs */}
      <Card>
        <CardHeader>
          <Heading size="md">Historique des synchronisations</Heading>
        </CardHeader>
        <CardBody p={0}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Type</Th>
                <Th>Statut</Th>
                <Th>Démarré</Th>
                <Th>Durée</Th>
                <Th>Enregistrements</Th>
                <Th>Résultats</Th>
              </Tr>
            </Thead>
            <Tbody>
              {logsData?.items.map((log) => (
                <Tr key={log.id}>
                  <Td>
                    <Badge variant="outline">
                      {getTypeLabel(log.sync_type)}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={getSyncStatusColor(log.status)}>
                      {getSyncStatusLabel(log.status)}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {formatDate(log.started_at)}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {log.status === 'running' ? (
                        <HStack spacing={2}>
                          <Spinner size="sm" />
                          <Text>En cours...</Text>
                        </HStack>
                      ) : (
                        formatDuration(log.duration_seconds)
                      )}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {log.total_records_processed}
                    </Text>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="green.600">
                        ✓ {log.new_records_created} créés
                      </Text>
                      <Text fontSize="sm" color="blue.600">
                        ↻ {log.existing_records_updated} mis à jour
                      </Text>
                      {log.records_with_errors > 0 && (
                        <Text fontSize="sm" color="red.600">
                          ✗ {log.records_with_errors} erreurs
                        </Text>
                      )}
                      {log.email_changes_detected > 0 && (
                        <Text fontSize="sm" color="orange.600">
                          ✉ {log.email_changes_detected} emails modifiés
                        </Text>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {logsData?.items.length === 0 && (
            <Box p={8} textAlign="center">
              <Text color="gray.500">Aucune synchronisation trouvée</Text>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Pagination */}
      {logsData && logsData.total_pages > 1 && (
        <Flex justify="center" mt={6}>
          <HStack>
            <Button
              isDisabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Précédent
            </Button>
            <Text px={4}>
              Page {page} sur {logsData.total_pages}
            </Text>
            <Button
              isDisabled={page === logsData.total_pages}
              onClick={() => setPage(page + 1)}
            >
              Suivant
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Sync Configuration Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Synchroniser les contacts CRM</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info" size="sm">
                <AlertIcon />
                <Box>
                  <Text fontSize="sm">
                    Cette synchronisation va récupérer tous les contacts depuis SuiteCRM.
                    L'opération devrait prendre environ 1-2 minutes.
                  </Text>
                </Box>
              </Alert>

              <Box w="full" p={4} bg="gray.50" borderRadius="md">
                <VStack align="start" spacing={2}>
                  <Text fontWeight="bold" fontSize="sm">Ce qui sera synchronisé:</Text>
                  <Text fontSize="sm">• Tous les contacts du CRM</Text>
                  <Text fontSize="sm">• Statuts VIP et informations de protocole</Text>
                  <Text fontSize="sm">• Tags de conférence de presse</Text>
                  <Text fontSize="sm">• Organisations et positions</Text>
                </VStack>
              </Box>

              {runningSync && (
                <Alert status="warning" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Une synchronisation est déjà en cours
                  </Text>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSync}
              isLoading={syncMutation.isPending}
              loadingText="Démarrage..."
              isDisabled={!!runningSync}
            >
              Synchroniser maintenant
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CRMSyncPage;